"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="23473" className="flex gap-2">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-9 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) return null;
  return <NavigationMenu>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors font-sans">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map(i => <div key={i} className="h-6 w-3/4 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) return null;
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-primary transition-colors font-sans" onClick={onItemClick}>
          {item.label}
        </Link>)}
      <div className="pt-4 border-t border-border/50">
        <Button className="w-full rounded-full bg-primary text-primary-foreground font-sans hover:bg-primary/90 transition-colors" data-tally-open="1AK1Qb" data-tally-layout="modal" data-tally-hide-title="1" data-tally-emoji-text="" data-tally-emoji-animation="wave">
          Free Property Report
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full border-b transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between !shadow-none !rounded">
          
          <div className="flex-shrink-0">
            <Link to="/HomePage" className="flex items-center gap-2" aria-label="Chubby Nest Home">
              <img src="https://relieved-eel.10web.cloud/wp-content/uploads/2026/03/purple-small-transparent-scaled.png" alt="Chubby Nest" className="h-12 object-contain" />
            </Link>
          </div>

          
          <div className="hidden md:flex items-center gap-6">
            <MenuProvider menu_id="16" className="flex items-center">
              <DesktopMenuConsumer />
            </MenuProvider>
            <div className="ml-4 pl-4 border-l border-border/30">
              <Button variant="default" size="sm" className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive gap-1.5 has-[>svg]:px-2.5 rounded-full h-6 py-0 px-6 text-primary-foreground shadow-md font-sans bg-primary disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90" data-tally-open="1AK1Qb" data-tally-layout="modal" data-tally-hide-title="1" data-tally-emoji-text="" data-tally-emoji-animation="wave" data-tally-width="450" data-tally-auto-close="3000">Free Property Report</Button>
            </div>
          </div>

          
          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" className="text-foreground hover:bg-accent/50 rounded-full">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      
      {isMobileMenuOpen && <div className="md:hidden absolute top-16 left-0 w-full bg-background/98 backdrop-blur-xl border-b border-border shadow-xl animate-in slide-in-from-top-2 duration-300">
          <MenuProvider menu_id="16">
            <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}